/**
 * 1.6.8 (D-1.6.8-1) — Needs your attention: "n Tech Support visits booked without an agreement", with the names —
 * only tenants who HAVE a booked Tech Support visit and no completed agreement ("If that tenant has been scheduled
 * and has not gotten their agreement signed, I need to know"). Each name opens that person's card in People, where
 * the agreements card offers Complete it for them / Send by email. One line on Today (it counts as one); the first
 * few names, the rest behind "Show all n". No such visit: the server sends no item and nothing is shown.
 * (1.6.7's "n tenants with no Tech Support agreement" — everybody without one — is gone.)
 */
import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { AttentionItem } from '@shared/api-types';
import { formatDayKey } from '@/lib/dates';
import { Button } from '@/components/ui/button';

const FIRST = 3;

export function TechNoAgreementItem({ item, dot }: { item: AttentionItem; dot: string }) {
  const { t, i18n } = useTranslation();
  const uid = useId();
  const [all, setAll] = useState(false);
  const people = item.people ?? [];
  const count = item.count ?? people.length;
  const shown = all ? people : people.slice(0, FIRST);
  // the server caps the names: how many tenants are beyond the list is not known by name, only that there are more
  const listed = people.reduce((n, p) => n + p.visits, 0);
  const beyond = count - listed;
  return (
    <>
      <li className="attn-head" data-testid="attn-tech-no-agreement">
        <span className="dot" style={{ background: dot }} aria-hidden="true" />
        <div className="t">
          <b data-testid="attn-tech-no-agreement-title">{t('tech168.attnTitle', { count })}</b>
          <span>{t('tech168.attnSub')}</span>
        </div>
        {people.length > FIRST ? (
          <Button size="sm" variant="secondary" aria-expanded={all} aria-controls={`${uid}-names`} onClick={() => setAll((v) => !v)} data-testid="attn-tech-no-agreement-all">
            {all ? t('tech168.attnShowFewer') : t('tech168.attnShowAll', { count: people.length })}
          </Button>
        ) : null}
      </li>
      <li className="attn-sub tech168-names">
        <ul id={`${uid}-names`} aria-label={t('tech168.attnTitle', { count })} data-testid="attn-tech-no-agreement-names">
          {shown.map((p) => {
            const who = [p.unit, p.name].filter(Boolean).join(' · ');
            const day = formatDayKey(p.dayKey, i18n.language);
            return (
              <li key={p.tenantId}>
                <Link to={`/people/${encodeURIComponent(p.tenantId)}`} aria-label={t('tech168.attnPersonFor', { who, day })} data-testid={`attn-tech-no-agreement-${p.tenantId}`}>
                  {t('tech168.attnPerson', { who, day })}
                </Link>
              </li>
            );
          })}
          {all && beyond > 0 ? (
            <li>
              <Link to="/people?program=ALL">{t('tech168.attnMore', { count: beyond })}</Link>
            </li>
          ) : null}
        </ul>
      </li>
    </>
  );
}
